import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchMaintenanceMasters,
  fetchMaintenanceRequests,
  fetchMaintenanceMetrics,
  deleteMaintenanceRequest,
  updateMaintenanceStatus,
} from "../api/maintenanceApi";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { useMaintenanceRole } from "../utils/useMaintenanceRole";
import { MAINTENANCE_RECORDS_CONTROL_SUBMODULE_SLUG } from "../utils/maintenancePermissionSlugs";
import MaintenanceDashboardMetricsSection from "../components/control/sections/MaintenanceDashboardMetricsSection";
import MaintenanceCreateSection from "../components/control/sections/MaintenanceCreateSection";
import MaintenanceDirectorySection, {
  MAINTENANCE_DIRECTORY_COLUMN_DEFS,
  createInitialMaintenanceColumnVisibility,
  maintenanceDirectoryCellValue,
  maintenanceDefaultDirectoryFilters,
} from "../components/control/sections/MaintenanceDirectorySection";
import MaintenanceDirectoryStatusMarkingBar, {
  buildMaintenanceBulkStatusSelectOptions,
} from "../components/control/sections/MaintenanceDirectoryStatusMarkingBar";
import MaintenanceQuickJumpSection from "../components/control/sections/MaintenanceQuickJumpSection";
import MaintenanceMastersSection from "../components/control/sections/MaintenanceMastersSection";
import MaintenanceRequestModalsSection from "../components/control/sections/MaintenanceRequestModalsSection";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

const DIRECTORY_PAGE_SIZE = 10;

/** Stable fallbacks so hook dependency arrays are not a new [] every render. */
const EMPTY_MAINTENANCE_TYPES = [];
const EMPTY_MAINTENANCE_STATUSES = [];

const EMPTY_METRICS = {
  total_requests: 0,
  pending_requests: 0,
  in_progress_requests: 0,
  completed_requests: 0,
  total_cost: 0,
};

function safeErrorText(err, fallback = "Something went wrong. Please try again.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

const toISODate = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [mm, dd, yyyy] = raw.split("/");
    return `${yyyy}-${mm}-${dd}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const MaintenanceControlPage = () => {
  const perms = useMaintenanceRole(MAINTENANCE_RECORDS_CONTROL_SUBMODULE_SLUG);
  const [masters, setMasters] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [directoryTotalPages, setDirectoryTotalPages] = useState(1);
  const [draftDirectoryFilters, setDraftDirectoryFilters] = useState(() =>
    maintenanceDefaultDirectoryFilters(),
  );
  const [appliedDirectoryFilters, setAppliedDirectoryFilters] = useState(() =>
    maintenanceDefaultDirectoryFilters(),
  );
  const [columnVisibility, setColumnVisibility] = useState(
    createInitialMaintenanceColumnVisibility,
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);

  const [showViewForm, setShowViewForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRequestForAssign, setSelectedRequestForAssign] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);

  const showCreate =
    !perms.permissionsLoading &&
    (perms.canCreate || (Boolean(editingRequest?.id) && perms.canEdit));
  const canEditActions = !perms.permissionsLoading && perms.canEdit;
  const canDeleteActions = !perms.permissionsLoading && perms.canDelete;

  const [selectedStatusIds, setSelectedStatusIds] = useState(() => new Set());
  const [bulkNewStatusId, setBulkNewStatusId] = useState("");
  const [applyingBulkStatus, setApplyingBulkStatus] = useState(false);

  const maintenanceTypes = masters?.maintenanceTypes ?? EMPTY_MAINTENANCE_TYPES;
  const maintenanceStatuses =
    masters?.maintenanceStatuses ?? EMPTY_MAINTENANCE_STATUSES;

  const onNotify = useCallback((msg, opts) => {
    if (!msg) {
      setErrorMsg("");
      setSuccessMsg("");
      return;
    }
    if (opts?.variant === "error") {
      setErrorMsg(msg);
      return;
    }
    setSuccessMsg(msg);
  }, []);

  const refreshMetrics = useCallback(async () => {
    try {
      const data = await fetchMaintenanceMetrics();
      setMetrics(data);
    } catch {
      setMetrics({ ...EMPTY_METRICS });
    }
  }, []);

  const loadMasters = useCallback(async () => {
    setLoadError("");
    try {
      const mData = await fetchMaintenanceMasters();
      setMasters(mData);
    } catch (e) {
      setLoadError(e.message || "Could not load data.");
    }
  }, []);

  const dirUserSearch = appliedDirectoryFilters.user_search;
  const dirColumn = appliedDirectoryFilters.column;
  const dirValue = appliedDirectoryFilters.value;

  const directoryApiFilters = useMemo(() => {
    const q = {};
    const name = String(dirUserSearch || "").trim();
    if (name) q.name = name;

    const col = dirColumn;
    const val = String(dirValue || "").trim();
    if (!col || !val) return q;

    if (col === "id") {
      const n = parseInt(String(val).replace(/\D/g, ""), 10);
      if (Number.isFinite(n)) q.request_id = String(n);
    }
    if (col === "created_at") {
      q.created_date = val.slice(0, 10);
    }
    if (col === "type") {
      const t = maintenanceTypes.find((x) => String(x.type_name) === val);
      if (t?.id != null) q.type_id = String(t.id);
    }
    if (col === "status") {
      const s = maintenanceStatuses.find((x) => String(x.status_name) === val);
      if (s?.id != null) q.status_id = String(s.id);
    }
    if (col === "asset_area") q.asset_area = val;
    if (col === "issue_description") q.issue_description = val;
    if (col === "reported_by") {
      const id = parseInt(String(val).trim(), 10);
      if (Number.isFinite(id)) q.reported_by_id = String(id);
    }
    if (col === "assigned_to_name") q.assigned_name_contains = val;
    if (col === "cost_incurred") {
      const n = Number(String(val).replace(/[^\d.-]/g, ""));
      if (Number.isFinite(n)) q.cost_match = String(n);
    }
    return q;
  }, [dirUserSearch, dirColumn, dirValue, maintenanceTypes, maintenanceStatuses]);

  const fetchRequestsList = useCallback(async () => {
    setLoadingRequests(true);
    setErrorMsg("");
    try {
      const result = await fetchMaintenanceRequests(
        directoryPage,
        DIRECTORY_PAGE_SIZE,
        directoryApiFilters,
      );
      setRequests(result.rows);
      setDirectoryTotal(result.total);
      setDirectoryTotalPages(result.totalPages);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingRequests(false);
    }
  }, [directoryPage, directoryApiFilters]);

  const getMaintenanceExportData = useCallback(() => {
    const visibleCols = MAINTENANCE_DIRECTORY_COLUMN_DEFS.filter(
      (c) => columnVisibility[c.id],
    );
    const headers = visibleCols.map((c) => c.label);
    const rows = requests.map((r) =>
      visibleCols.map((c) => maintenanceDirectoryCellValue(c.id, r, masters)),
    );
    return { headers, rows };
  }, [requests, columnVisibility, masters]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    loadMasters();
  }, [loadMasters, perms.permissionsLoading, perms.canView]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    let cancelled = false;
    (async () => {
      setLoadingMetrics(true);
      try {
        const met = await fetchMaintenanceMetrics();
        if (!cancelled) setMetrics(met);
      } catch {
        if (!cancelled) setMetrics({ ...EMPTY_METRICS });
      } finally {
        if (!cancelled) setLoadingMetrics(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [perms.permissionsLoading, perms.canView]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedDirectoryFilters({ ...draftDirectoryFilters });
      setDirectoryPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [draftDirectoryFilters]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    fetchRequestsList();
  }, [fetchRequestsList, perms.permissionsLoading, perms.canView]);

  useEffect(() => {
    setSelectedStatusIds(new Set());
  }, [directoryPage]);

  useEffect(() => {
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return () => {
      window.history.scrollRestoration = prev;
    };
  }, []);

  const handleRequestCreated = useCallback(() => {
    refreshMetrics();
    fetchRequestsList();
  }, [refreshMetrics, fetchRequestsList]);

  const handleRequestUpdated = useCallback(() => {
    refreshMetrics();
    fetchRequestsList();
  }, [refreshMetrics, fetchRequestsList]);

  const toggleSelectRequest = useCallback((id) => {
    const nid = Number(id);
    if (!Number.isFinite(nid)) return;
    setSelectedStatusIds((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) next.delete(nid);
      else next.add(nid);
      return next;
    });
  }, []);

  const toggleSelectAllDirectoryPage = useCallback(
    (checked) => {
      setSelectedStatusIds((prev) => {
        const next = new Set(prev);
        const pageIds = requests
          .map((r) => Number(r.id))
          .filter((n) => Number.isFinite(n));
        if (checked) {
          pageIds.forEach((i) => next.add(i));
        } else {
          pageIds.forEach((i) => next.delete(i));
        }
        return next;
      });
    },
    [requests],
  );

  const bulkStatusSelectOptions = useMemo(
    () => buildMaintenanceBulkStatusSelectOptions(maintenanceStatuses),
    [maintenanceStatuses],
  );

  const handleApplyBulkStatus = useCallback(async () => {
    if (selectedStatusIds.size === 0) {
      onNotify("Select at least one request.", { variant: "error" });
      return;
    }
    if (!bulkNewStatusId) {
      onNotify("Choose a status to apply.", { variant: "error" });
      return;
    }
    setApplyingBulkStatus(true);
    try {
      const newId = parseInt(bulkNewStatusId, 10);
      let applied = 0;
      for (const requestId of selectedStatusIds) {
        await updateMaintenanceStatus({
          request_id: requestId,
          new_status_id: newId,
          updated_by: 1,
          remarks: null,
        });
        applied += 1;
      }
      onNotify(
        applied === 1 ? "Status updated for 1 request." : `Status updated for ${applied} requests.`,
      );
      setSelectedStatusIds(new Set());
      setBulkNewStatusId("");
      handleRequestUpdated();
    } catch (err) {
      onNotify(safeErrorText(err, "Failed to update status."), { variant: "error" });
    } finally {
      setApplyingBulkStatus(false);
    }
  }, [selectedStatusIds, bulkNewStatusId, onNotify, handleRequestUpdated]);

  const directoryRowSelection = useMemo(
    () =>
      canEditActions
        ? {
            enabled: true,
            selectedIds: selectedStatusIds,
            onToggle: toggleSelectRequest,
            onToggleAll: toggleSelectAllDirectoryPage,
          }
        : null,
    [
      canEditActions,
      selectedStatusIds,
      toggleSelectRequest,
      toggleSelectAllDirectoryPage,
    ],
  );

  const directoryApprovals = useMemo(
    () =>
      canEditActions ? (
        <MaintenanceDirectoryStatusMarkingBar
          selectedCount={selectedStatusIds.size}
          statusOptions={bulkStatusSelectOptions}
          bulkNewStatusId={bulkNewStatusId}
          onBulkNewStatusIdChange={setBulkNewStatusId}
          applyingBulkStatus={applyingBulkStatus}
          onMark={handleApplyBulkStatus}
          onClearSelection={() => setSelectedStatusIds(new Set())}
        />
      ) : null,
    [
      canEditActions,
      selectedStatusIds.size,
      bulkNewStatusId,
      bulkStatusSelectOptions,
      applyingBulkStatus,
      handleApplyBulkStatus,
    ],
  );

  const handleViewForm = useCallback((request) => {
    setSelectedRequest(request);
    setShowViewForm(true);
  }, []);

  const handleCloseViewForm = useCallback(() => {
    setShowViewForm(false);
    setSelectedRequest(null);
  }, []);

  const handleEditForm = useCallback(
    (request) => {
      if (!canEditActions) return;
      const startISO = toISODate(request?.start_date);
      const completionISO = toISODate(request?.completion_date);
      const hasInvalidRange = Boolean(
        startISO && completionISO && completionISO < startISO,
      );
      if (hasInvalidRange) {
        onNotify("Completion date cannot be before start date.", { variant: "error" });
      }
      setEditingRequest({
        ...request,
        start_date: startISO,
        completion_date: hasInvalidRange ? "" : completionISO,
      });
      window.requestAnimationFrame(() => {
        document.getElementById("md-create")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    },
    [onNotify, canEditActions],
  );

  const handleCancelDirectoryEdit = useCallback(() => {
    setEditingRequest(null);
  }, []);

  const handleAssignForm = useCallback((request) => {
    setSelectedRequestForAssign({ ...request });
    setShowAssignForm(true);
  }, []);

  const handleCloseAssignForm = useCallback(() => {
    setShowAssignForm(false);
    setSelectedRequestForAssign(null);
  }, []);

  const handleDeleteRequest = useCallback(
    async (request) => {
      if (
        !window.confirm(
          "Delete this maintenance request? It will be marked inactive.",
        )
      ) {
        return;
      }
      try {
        await deleteMaintenanceRequest(request.id);
        setEditingRequest((cur) =>
          cur != null && String(cur.id) === String(request.id) ? null : cur,
        );
        onNotify("Maintenance request deleted successfully.");
        handleRequestUpdated();
      } catch (error) {
        onNotify(error.message || "Failed to delete request", { variant: "error" });
      }
    },
    [onNotify, handleRequestUpdated],
  );

  const QUICK_JUMP_ITEMS = useMemo(() => {
    const items = [];
    if (perms.canView) {
      items.push({ key: "stats", label: "Performance overview", targetId: "md-stats" });
      items.push({ key: "list", label: "Directory", targetId: "md-list" });
    }
    if (showCreate) {
      items.push({ key: "create", label: "New request", targetId: "md-create" });
    }
    if (perms.canManageMasters) {
      items.push({ key: "masters", label: "Master tables", targetId: "md-masters" });
    }
    return items;
  }, [perms.canView, perms.canManageMasters, showCreate]);

  const normalizedLoadError = loadError.trim().toLowerCase();
  const normalizedErrorMsg = errorMsg.trim().toLowerCase();
  const showLoadErrorBanner = Boolean(
    loadError && (!errorMsg || normalizedLoadError !== normalizedErrorMsg),
  );

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative area</p>
          <h2 className="user-builder-hero__title">Maintenance management</h2>
          <p className="user-builder-hero__sub">
            Track requests, assignments, and costs. In the directory, use <strong>Status marking</strong>{" "}
            (checkboxes + status + Mark), or open a row to <strong>view</strong>, <strong>edit</strong> (form
            above), or <strong>assign</strong>. Search by reporter or assignee name and refine by column.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />
      <GlobalFeedback type="error" text={perms.permissionsError} role="alert" />
      {showLoadErrorBanner ? (
        <GlobalFeedback type="error" text={loadError} />
      ) : null}
      {perms.permissionsLoading ? (
        <p className="schedule-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !perms.canView && !perms.canManageMasters ? (
        <GlobalFeedback
          type="warning"
          text="No permission for maintenance control. Enable View on maintenance-records-control in User builder, then sign in again."
          role="status"
        />
      ) : null}

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && perms.canView ? (
            <MaintenanceDashboardMetricsSection
              metrics={metrics}
              loadingMetrics={loadingMetrics}
            />
          ) : null}

          {!perms.permissionsLoading && showCreate ? (
            <MaintenanceCreateSection
              mastersFromParent
              masters={masters}
              mastersLoadError={loadError}
              onNotify={onNotify}
              onRequestCreated={handleRequestCreated}
              editingRequest={editingRequest}
              onCancelEdit={handleCancelDirectoryEdit}
            />
          ) : null}

          {!perms.permissionsLoading && perms.canView ? (
            <MaintenanceDirectorySection
              requests={requests}
              masters={masters}
              draftDirectoryFilters={draftDirectoryFilters}
              onDraftDirectoryFiltersChange={setDraftDirectoryFilters}
              onClearDirectoryFilters={() =>
                setDraftDirectoryFilters(maintenanceDefaultDirectoryFilters())
              }
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              getMaintenanceExportData={
                perms.canExport ? getMaintenanceExportData : undefined
              }
              loadingRequests={loadingRequests}
              directoryPage={directoryPage}
              directoryTotalPages={directoryTotalPages}
              directoryTotal={directoryTotal}
              directoryPageSize={DIRECTORY_PAGE_SIZE}
              onDirectoryPageChange={setDirectoryPage}
              onView={handleViewForm}
              onEdit={canEditActions ? handleEditForm : undefined}
              onAssign={canEditActions ? handleAssignForm : undefined}
              onDelete={canDeleteActions ? handleDeleteRequest : undefined}
              approvals={directoryApprovals}
              rowSelection={directoryRowSelection}
            />
          ) : null}

          {!perms.permissionsLoading && perms.canManageMasters ? (
            <MaintenanceMastersSection onMastersChange={loadMasters} onNotify={onNotify} />
          ) : null}
        </div>

        {QUICK_JUMP_ITEMS.length > 0 ? (
          <MaintenanceQuickJumpSection
            asideAriaLabel="Maintenance dashboard sections"
            items={QUICK_JUMP_ITEMS}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>
    
      <MaintenanceRequestModalsSection
        masters={masters}
        onNotify={onNotify}
        showViewForm={showViewForm}
        selectedRequest={selectedRequest}
        onCloseViewForm={handleCloseViewForm}
        showAssignForm={showAssignForm}
        selectedRequestForAssign={selectedRequestForAssign}
        setSelectedRequestForAssign={setSelectedRequestForAssign}
        onCloseAssignForm={handleCloseAssignForm}
        onRequestUpdated={handleRequestUpdated}
      />
    </div>
  );
};

export default MaintenanceControlPage;
